import React from "react";
import DetalleCard from "~business/Cards/DetalleCard";
import { useDispatch, useSelector } from "react-redux";
import ScheduleIcon from "@material-ui/icons/Schedule";
import ItemInputCard from "~business/Cards/ItemInputCard";
import ItemDateCard from "~business/Cards/ItemDateCard";
import getProvincias from "./utils/getProvincias";
import MultipleItemSelectorCard from "~business/Cards/MultipleItemSelectorCard";

import {
  setFecha,
  setHora,
  setDireccion,
  setLocalidad,
  setProvincia,
  setCodigoPostal,
  setFocusLocalidad,
  setFocusCodigoPostal,
  fetchLocalidadesCompleted,
  fetchCodigosPostalesCompleted,
  setSubmitting,
  fetchLocalidades,
  fetchCodigosPostales,
} from "../actions";
import { toPascalCase } from "~libs/stringsPascalCase";

const useReduxSelector = () =>
  useSelector((state) => ({
    fecha: state.cargaSiniestroPublicaReducer.fechaSelected,
    hora: state.cargaSiniestroPublicaReducer.horaSelected,
    direccion: state.cargaSiniestroPublicaReducer.direccionSelected,
    localidad: state.cargaSiniestroPublicaReducer.localidadSelected,
    provincia: state.cargaSiniestroPublicaReducer.provinciaSelected,
    codigoPostal: state.cargaSiniestroPublicaReducer.codigoPostalSelected,
    localidades: state.cargaSiniestroPublicaReducer.localidades,
    codigosPostales: state.cargaSiniestroPublicaReducer.codigosPostales,
  }));

const title = "Fecha y Lugar";

const HORA_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;

const isValidHora = (raw) => {
  const v = (raw ?? "").trim();
  return HORA_REGEX.test(v);
};

const normalizeHora = (raw) => {
  let v = (raw ?? "").toString().trim();

  v = v.replace(/[^\d:]/g, "");

  if (/^\d{4}$/.test(v)) return `${v.slice(0, 2)}:${v.slice(2)}`;

  if (/^\d{3}$/.test(v)) return `0${v[0]}:${v.slice(1).padStart(2, "0")}`;

  if (/^\d{1,2}:\d{1,2}$/.test(v)) {
    const [h, m] = v.split(":");
    return `${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
  }

  return v;
};

const FechaLugarCard = ({ isLoading }) => {
  const dispatch = useDispatch();

  const {
    fecha,
    hora,
    direccion,
    localidad,
    provincia,
    codigoPostal,
    localidades,
    codigosPostales,
  } = useReduxSelector();

  let timer = null;

  const handleChangeFecha = (fecha) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setFecha(fecha));
    }, 200);
  };

  const handleChangeHora = (hora) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setHora(hora));
    }, 200);
  };

  const handleChangeDireccion = (direccion) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setDireccion(direccion));
    }, 200);
  };

  const handleChangeProvincia = (provincia) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setProvincia(provincia));

      dispatch(setLocalidad({ label: "", value: "" }));
      dispatch(setCodigoPostal({ label: "", value: "" }));

      dispatch(fetchLocalidadesCompleted([]));
      dispatch(fetchCodigosPostalesCompleted([]));

      dispatch(fetchLocalidades(provincia.value));

      setTimeout(() => {
        dispatch(setFocusLocalidad(true));
      }, 100);
    }, 200);
  };

  const handleChangeLocalidad = (localidad) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setLocalidad(localidad));

      dispatch(setCodigoPostal({ label: "", value: "" }));
      dispatch(fetchCodigosPostalesCompleted([]));

      if (provincia && provincia.searchId) {
        dispatch(fetchCodigosPostales(provincia.searchId, localidad.value));
      }

      setTimeout(() => {
        dispatch(setFocusCodigoPostal(true));
      }, 100);
    }, 200);
  };

  const handleChangeCodigoPostal = (codigoPostal) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setCodigoPostal(codigoPostal));
    }, 200);
  };

  const handleBlur = () => {
    dispatch(setSubmitting(false));
  };

  const provincias = getProvincias();

  return (
    <>
      <DetalleCard title={title} IconHeader={ScheduleIcon}>
        <ItemDateCard
          id={"fecha"}
          isLoading={isLoading}
          label={"Fecha"}
          value={fecha.value}
          onChange={handleChangeFecha}
          onBlur={handleBlur}
          focus={fecha.focus}
          maxDate={new Date()}
          minDate={new Date("2000-01-01")}
          onValidate={(value) =>
            value >= new Date("2000-01-01") && value <= new Date()
          }
          required
        />
        <ItemInputCard
          id={"hora"}
          isLoading={isLoading}
          label={"Hora"}
          value={hora.value}
          onChange={handleChangeHora}
          onBlur={handleBlur}
          focus={hora.focus}
          placeholder={"Ej: 20:45"}
          required
          maxLength={5}
          customValidate={isValidHora}
          invalidText={"Formato inválido. Usá HH:mm (00–23:59)."}
          formatValue={normalizeHora}
        />
        <ItemInputCard
          id={"direccion"}
          isLoading={isLoading}
          label={"Calle, altura e intersección"}
          value={direccion.value}
          onChange={handleChangeDireccion}
          onBlur={handleBlur}
          focus={direccion.focus}
          required
          formatValue={toPascalCase}
          multiline
        />

        <MultipleItemSelectorCard
          id={"provincia"}
          isLoading={isLoading}
          label={"Provincia"}
          value={provincia.value}
          onChange={handleChangeProvincia}
          placeholder={"Seleccionar"}
          onBlur={handleBlur}
          focus={provincia.focus}
          tooltipContent={"Seleccionar Provincia"}
          items={provincias}
        />

        <MultipleItemSelectorCard
          id={"localidad"}
          isLoading={isLoading}
          label={"Localidad"}
          value={localidad.value}
          onChange={handleChangeLocalidad}
          placeholder={"Seleccionar"}
          onBlur={handleBlur}
          focus={localidad.focus}
          tooltipContent={"Seleccionar Localidad"}
          items={localidades}
        />

        <MultipleItemSelectorCard
          id={"codigoPostal"}
          isLoading={isLoading}
          label={"Código Postal"}
          value={codigoPostal.value}
          onChange={handleChangeCodigoPostal}
          placeholder={"Seleccionar"}
          onBlur={handleBlur}
          focus={codigoPostal.focus}
          tooltipContent={"Seleccionar Código Postal"}
          items={codigosPostales}
        />
      </DetalleCard>
    </>
  );
};

export default FechaLugarCard;
